/* GCS Global – Relaunch David (Baustein 1+2)
   window.GCS Namespace: formatMoney, Scroll-Lock, Fokus-Trap, Toast, <gcs-drawer>, <gcs-header>,
   <gcs-predictive-search>, Auto-Submit für Selects. Lädt mit defer nach constants.js, pubsub.js, global.js.
   Schnittstellen: docs/build-contract.md Abschnitt 6, Beispiele in docs/design-system.md. */
(() => {
  const GCS = (window.GCS = window.GCS || {});
  GCS.settings = GCS.settings || {};

  /* Hilfen ---------------------------------------------------------------- */
  GCS.debounce = (fn, wait = 200) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  GCS.routes = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return Object.assign(
      {
        root,
        cart: root + 'cart',
        search: root + 'search',
        predictiveSearch: root + 'search/suggest',
      },
      GCS.settings.routes || {}
    );
  };

  /* Geld: GCS.formatMoney(cents[, format]) nach GCS.settings.moneyFormat ---- */
  const formatNumber = (cents, precision, thousands, decimal) => {
    const fixed = (cents / 100).toFixed(precision);
    const parts = fixed.split('.');
    const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts[1] ? int + decimal + parts[1] : int;
  };
  GCS.formatMoney = (cents, format) => {
    let value = typeof cents === 'string' ? cents.replace('.', '') : cents;
    value = Number(value) || 0;
    const fmt = format || GCS.settings.moneyFormat || '{{amount_with_comma_separator}} €';
    const re = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(re);
    if (!match) return fmt;
    let out;
    switch (match[1]) {
      case 'amount':
        out = formatNumber(value, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        out = formatNumber(value, 0, ',', '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = formatNumber(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = formatNumber(value, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        out = formatNumber(value, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        out = formatNumber(value, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        out = formatNumber(value, 2, ' ', '.');
        break;
      default:
        out = formatNumber(value, 2, '.', ',');
    }
    return fmt.replace(re, out);
  };

  /* Scroll-Lock (gezählt, damit verschachtelte Overlays sich nicht stören) */
  let lockCount = 0;
  GCS.lockScroll = () => {
    lockCount += 1;
    if (lockCount > 1) return;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbar > 0) document.body.style.paddingRight = scrollbar + 'px';
    document.documentElement.classList.add('gcs-locked');
  };
  GCS.unlockScroll = () => {
    if (lockCount === 0) return;
    lockCount -= 1;
    if (lockCount > 0) return;
    document.documentElement.classList.remove('gcs-locked');
    document.body.style.paddingRight = '';
  };

  /* Fokus-Trap: GCS.trapFocus(container, elementToFocus), GCS.releaseFocus(returnTo) */
  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
  GCS.focusable = (root) =>
    Array.from(root.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden'
    );
  let activeTrap = null;
  GCS.trapFocus = (container, elementToFocus) => {
    GCS.releaseFocus();
    // Offenes Modal (<dialog>, z. B. Garantie-Pop-up über dem Drawer) führt den Fokus selbst
    const inDialog = (el) => !!(el && el.closest && el.closest('dialog[open]'));
    const onKeydown = (event) => {
      if (event.key !== 'Tab' || inDialog(document.activeElement)) return;
      const els = GCS.focusable(container);
      if (!els.length) {
        event.preventDefault();
        container.focus();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !container.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !container.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    const onFocusin = (event) => {
      if (container.contains(event.target) || inDialog(event.target)) return;
      const els = GCS.focusable(container);
      (els[0] || container).focus();
    };
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('focusin', onFocusin);
    activeTrap = { onKeydown, onFocusin };
    const target = elementToFocus || GCS.focusable(container)[0] || container;
    if (target && target.focus) target.focus({ preventScroll: true });
  };
  GCS.releaseFocus = (returnTo) => {
    if (activeTrap) {
      document.removeEventListener('keydown', activeTrap.onKeydown);
      document.removeEventListener('focusin', activeTrap.onFocusin);
      activeTrap = null;
    }
    if (returnTo && returnTo.focus && document.contains(returnTo)) returnTo.focus({ preventScroll: true });
  };

  /* Toast: GCS.toast('Text', { type: 'error' }) --------------------------- */
  let toastEl;
  let toastTimer;
  GCS.toast = (message, { type = 'info', duration = 3600 } = {}) => {
    if (!message) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'gcs-toast';
      document.body.appendChild(toastEl);
    }
    toastEl.setAttribute('role', type === 'error' ? 'alert' : 'status');
    toastEl.classList.toggle('gcs-toast--error', type === 'error');
    toastEl.textContent = message;
    requestAnimationFrame(() => toastEl.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), duration);
  };

  /* <gcs-drawer id="…" class="gcs-drawer gcs-drawer--left|--right|--bottom|--top">
       <div class="gcs-drawer__overlay" data-gcs-close></div>
       <div class="gcs-drawer__panel gcs-scheme--…" role="dialog" aria-modal="true" aria-label="…" tabindex="-1">…</div>
     </gcs-drawer>
     Öffnen: <button data-gcs-open="ID" aria-controls="ID" aria-expanded="false"> oder element.open(opener).
     Schließen: [data-gcs-close], Overlay, Escape. Events: gcs:drawer:open / gcs:drawer:close (detail.id). */
  class GcsDrawer extends HTMLElement {
    connectedCallback() {
      if (this._gcsReady) return;
      this._gcsReady = true;
      this.panel = this.querySelector('.gcs-drawer__panel') || this;
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-gcs-close]')) {
          event.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          event.stopPropagation();
          this.close();
        }
      });
    }

    disconnectedCallback() {
      if (this.isOpen) this.close({ restoreFocus: false });
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    setOpeners(expanded) {
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((el) => {
        el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    }

    open(opener) {
      if (this.isOpen) return;
      GCS.drawers.forEach((drawer) => drawer !== this && drawer.isOpen && drawer.close({ restoreFocus: false }));
      this.opener = opener || document.activeElement;
      this.setAttribute('open', '');
      this.setOpeners(true);
      GCS.lockScroll();
      const focusTarget = this.querySelector('[data-gcs-autofocus]') || this.panel;
      GCS.trapFocus(this.panel, focusTarget);
      this.dispatchEvent(new CustomEvent('gcs:drawer:open', { bubbles: true, detail: { id: this.id } }));
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen) return;
      this.removeAttribute('open');
      this.setOpeners(false);
      GCS.unlockScroll();
      GCS.releaseFocus(restoreFocus ? this.opener : null);
      this.dispatchEvent(new CustomEvent('gcs:drawer:close', { bubbles: true, detail: { id: this.id } }));
    }

    toggle(opener) {
      this.isOpen ? this.close() : this.open(opener);
    }
  }
  GCS.DrawerElement = GcsDrawer;
  GCS.drawers = new Set();
  if (!customElements.get('gcs-drawer')) {
    customElements.define(
      'gcs-drawer',
      class extends GcsDrawer {
        connectedCallback() {
          super.connectedCallback();
          GCS.drawers.add(this);
        }
        disconnectedCallback() {
          super.disconnectedCallback();
          GCS.drawers.delete(this);
        }
      }
    );
  }
  GCS.openDrawer = (id, opener) => {
    const el = document.getElementById(id);
    if (el && typeof el.open === 'function') el.open(opener);
  };
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-gcs-open]');
    if (!opener) return;
    const target = document.getElementById(opener.dataset.gcsOpen);
    if (!target || typeof target.open !== 'function') return;
    event.preventDefault();
    target.toggle ? target.toggle(opener) : target.open(opener);
  });

  /* Garantie-Pop-up (Runde D, 04.10.2026): <dialog id="GcsGuaranteeModal"> aus snippets/gcs-guarantee-modal.liquid.
     Öffnet über jedes Element mit [data-gcs-guarantee-open] (Links behalten href als Rückfall ohne JS/ohne <dialog>).
     Schließen: Button [data-gcs-guarantee-close], Escape, Klick außerhalb. Fokus zurück zum Auslöser, Scroll-Lock
     gezählt über GCS.lockScroll (offener Warenkorb-Drawer bleibt gesperrt). Escape geht nicht an den Drawer darunter.
     API: GCS.guarantee.open(opener), GCS.guarantee.close(). */
  const guaranteeModal = () => document.getElementById('GcsGuaranteeModal');
  let guaranteeOpener = null;
  let guaranteeLocked = false;
  const bindGuarantee = (dialog) => {
    if (!dialog || dialog._gcsBound) return;
    dialog._gcsBound = true;
    dialog.addEventListener('click', (event) => {
      // Klick auf die Fläche außerhalb des Panels trifft das <dialog> selbst (Panel füllt es ohne Innenabstand)
      if (event.target === dialog || event.target.closest('[data-gcs-guarantee-close]')) {
        event.preventDefault();
        dialog.close();
      }
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') event.stopPropagation(); // nicht zusätzlich den Warenkorb-Drawer schließen
    });
    dialog.addEventListener('close', () => {
      if (guaranteeLocked) {
        guaranteeLocked = false;
        GCS.unlockScroll();
      }
      const opener = guaranteeOpener;
      guaranteeOpener = null;
      if (opener && opener.focus && document.contains(opener)) opener.focus({ preventScroll: true });
    });
  };
  GCS.guarantee = {
    open(opener) {
      const dialog = guaranteeModal();
      if (!dialog || typeof dialog.showModal !== 'function') return false;
      bindGuarantee(dialog);
      if (dialog.open) return true;
      guaranteeOpener = opener || document.activeElement;
      try {
        dialog.showModal();
      } catch (e) {
        return false;
      }
      if (!guaranteeLocked) {
        guaranteeLocked = true;
        GCS.lockScroll();
      }
      const close = dialog.querySelector('[data-gcs-guarantee-close]');
      if (close) close.focus({ preventScroll: true });
      return true;
    },
    close() {
      const dialog = guaranteeModal();
      if (dialog && dialog.open) dialog.close();
    },
  };
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest && event.target.closest('[data-gcs-guarantee-open]');
    if (!trigger || event.defaultPrevented) return;
    if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return; // neuer Tab: Garantie-Seite
    if (GCS.guarantee.open(trigger)) event.preventDefault();
  });

  /* <gcs-header>: blendet beim Runterscrollen aus, beim Hochscrollen ein.
     Setzt --gcs-header-offset (sichtbare Header-Höhe oder 0) für eigene Sticky-Elemente. */
  class GcsHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this.parentElement;
      this.lastY = Math.max(window.scrollY, 0);
      this.isHiddenState = false;
      this.measure();
      this.onScroll = this.onScroll.bind(this);
      this.onResize = GCS.debounce(() => this.measure(), 150);
      /* Scroll-Events kommen ohnehin höchstens einmal pro Frame; die Logik ist billig (kein Layout-Lesen). */
      this.scrollHandler = () => this.onScroll();
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.addEventListener('focusin', () => this.show());
      this.initNav();
      this.setOffset();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.scrollHandler);
      window.removeEventListener('resize', this.onResize);
    }

    measure() {
      this.height = this.offsetHeight;
      document.documentElement.style.setProperty('--gcs-header-height', this.height + 'px');
      this.setOffset();
    }

    setOffset() {
      document.documentElement.style.setProperty('--gcs-header-offset', this.isHiddenState ? '0px' : this.height + 'px');
    }

    show() {
      if (!this.isHiddenState) return;
      this.isHiddenState = false;
      this.classList.remove('is-hidden');
      this.setOffset();
    }

    hide() {
      if (this.isHiddenState || this.querySelector('.is-open')) return;
      this.isHiddenState = true;
      this.classList.add('is-hidden');
      this.closeNav();
      this.setOffset();
    }

    onScroll() {
      if (document.documentElement.classList.contains('gcs-locked')) return;
      const y = Math.max(window.scrollY, 0);
      const delta = y - this.lastY;
      const threshold = this.height * 3;
      if (y <= threshold) this.show();
      else if (delta > 6) this.hide();
      else if (delta < -6) this.show();
      if (Math.abs(delta) > 6 || y <= threshold) this.lastY = y;
    }

    /* Desktop-Navigation: Dropdown / Mega-Menü per Hover (Maus), Klick und Tastatur */
    initNav() {
      this.navItems = Array.from(this.querySelectorAll('[data-gcs-nav-item]'));
      this.navItems.forEach((item) => {
        const toggle = item.querySelector('[data-gcs-nav-toggle]');
        const panel = toggle && document.getElementById(toggle.getAttribute('aria-controls'));
        if (!toggle || !panel) return;
        let timer;
        const open = () => {
          clearTimeout(timer);
          this.closeNav(item);
          item.classList.add('is-open');
          toggle.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
        };
        const close = () => {
          clearTimeout(timer);
          item.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
          panel.hidden = true;
        };
        item._gcsClose = close;
        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(timer);
          timer = setTimeout(open, 90);
        });
        item.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(timer);
          timer = setTimeout(close, 160);
        });
        toggle.addEventListener('click', () => (item.classList.contains('is-open') ? close() : open()));
        item.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && item.classList.contains('is-open')) {
            close();
            toggle.focus();
          }
        });
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) close();
        });
      });
    }

    closeNav(except) {
      (this.navItems || []).forEach((item) => item !== except && item._gcsClose && item._gcsClose());
    }
  }
  if (!customElements.get('gcs-header')) customElements.define('gcs-header', GcsHeader);

  /* <gcs-predictive-search>: Vorschläge über /search/suggest mit Section gcs-predictive-search */
  class GcsPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-gcs-ps-results]');
      this.defaults = this.querySelector('[data-gcs-ps-default]');
      this.status = this.querySelector('[data-gcs-ps-status]');
      this.cache = new Map();
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', GCS.debounce(() => this.search(), 220));
      this.input.form &&
        this.input.form.addEventListener('submit', (event) => {
          if (!this.input.value.trim()) event.preventDefault();
        });
    }

    reset() {
      this.results.innerHTML = '';
      this.results.hidden = true;
      if (this.defaults) this.defaults.hidden = false;
    }

    async search() {
      const q = this.input.value.trim();
      if (!q) return this.reset();
      if (this.cache.has(q)) return this.render(this.cache.get(q));
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.results.classList.add('is-loading');
      const params = new URLSearchParams({
        q,
        section_id: 'gcs-predictive-search',
        'resources[type]': 'product,collection,page',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
      });
      try {
        const response = await fetch(`${GCS.routes().predictiveSearch}?${params.toString()}`, {
          signal: this.controller.signal,
        });
        if (!response.ok) throw new Error(String(response.status));
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.getElementById('shopify-section-gcs-predictive-search');
        const html = section ? section.innerHTML : '';
        this.cache.set(q, html);
        if (this.input.value.trim() === q) this.render(html);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.results.classList.remove('is-loading');
      }
    }

    render(html) {
      this.results.classList.remove('is-loading');
      this.results.innerHTML = html;
      this.results.hidden = false;
      if (this.defaults) this.defaults.hidden = true;
      if (this.status) {
        const wrapper = this.results.querySelector('[data-gcs-ps-total]');
        const total = wrapper ? Number(wrapper.dataset.gcsPsTotal) : 0;
        this.status.textContent = total ? `${total} Vorschläge` : 'Keine Vorschläge';
      }
    }
  }
  if (!customElements.get('gcs-predictive-search')) customElements.define('gcs-predictive-search', GcsPredictiveSearch);

  /* Footer-Akkordeons (Impeccable G9): <details data-gcs-footer-acc> mobil zu und klappbar, ab 750 px offen und fest.
     Liquid rendert sie offen (ohne JavaScript bleiben alle Links sichtbar). */
  const footerQuery = window.matchMedia('(min-width: 750px)');
  const syncFooterAccs = () => {
    document.querySelectorAll('[data-gcs-footer-acc]').forEach((acc) => {
      acc.open = footerQuery.matches;
      const summary = acc.querySelector(':scope > summary');
      if (summary) summary.tabIndex = footerQuery.matches ? -1 : 0;
    });
  };
  syncFooterAccs();
  if (footerQuery.addEventListener) footerQuery.addEventListener('change', syncFooterAccs);
  document.addEventListener('click', (event) => {
    const summary = event.target.closest && event.target.closest('[data-gcs-footer-acc] > summary');
    if (summary && footerQuery.matches) event.preventDefault();
  });
  document.addEventListener('shopify:section:load', syncFooterAccs);

  /* Auto-Submit für Selects (z. B. Länderauswahl im Footer): <select data-gcs-autosubmit> */
  document.addEventListener('change', (event) => {
    const el = event.target.closest && event.target.closest('[data-gcs-autosubmit]');
    if (el && el.form) el.form.submit();
  });
})();
